import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Bell, Settings, User, Coins, Home } from 'lucide-react';
import { useMe } from '../../hooks/useAuth';
import { useMyCharacter } from '../../hooks/useCharacter';
import { useLanguage } from '../../contexts/LanguageContext';
import { soundSynth } from '@/utils/soundSynth';
import type { RootState } from '../../store/store';

export function Header() {
    const { data: apiUser } = useMe();
    const { user: authUser } = useSelector((state: RootState) => state.auth);
    const { data: character, isLoading: charLoading } = useMyCharacter();
    const { t } = useLanguage();
    
    // API 데이터가 우선, 없으면 Redux 저장 데이터 사용
    const user = apiUser || authUser;
    
    // 권한 체크: user 객체의 role 확인 (apiUser 또는 authUser)
    const userRole = (user as any)?.role;
    const isAdmin = user && ['PLATFORM_ADMIN', 'ADMIN', 'SUPER_ADMIN'].includes(userRole);

    return (
        <header
            className="sticky top-0 z-[100] bg-[var(--bg-main)]/90 backdrop-blur-md px-3 md:px-6 pb-3 flex items-center justify-between border-b border-[var(--border-subtle)] shadow-[0_2px_10px_rgba(0,0,0,0.3)]"
            // 모바일 앱(Capacitor)에서 상태바/노치에 헤더 상단이 잘리지 않도록 안전영역 패딩 적용
            // (일반 브라우저에서는 env() = 0 이라 기존 py-3과 동일)
            style={{ paddingTop: 'calc(0.75rem + env(safe-area-inset-top, 0px))' }}
        >
            <div className="flex items-center gap-2 md:gap-3">
                {/* Home Button Widget */}
                <Link 
                    to="/home" 
                    onClick={() => soundSynth.playClick()}
                    className="p-2 bg-[#1b1b24] hover:bg-[#252538] rounded-xl border border-white/5 hover:border-yellow-500/30 transition-all duration-300 group shadow-inner"
                    title="홈으로 이동"
                >
                    <Home size={18} className="text-[var(--text-muted)] group-hover:text-yellow-400 transition-colors" />
                </Link>
                
                {/* Slim Divider */}
                <div className="w-[1px] h-5 bg-white/10"></div>
 
                {/* Profile Card Link */}
                <Link 
                    to="/profile" 
                    onClick={() => soundSynth.playClick()}
                    className="flex items-center gap-1.5 px-1.5 py-1 rounded-2xl hover:bg-white/5 transition-all duration-300 group shrink-0"
                >
                    <div className="w-8 h-8 rounded-full border border-white/10 bg-[#1e1e1e] flex items-center justify-center font-bold overflow-hidden group-hover:border-yellow-500/50 transition-colors relative shrink-0">
                        {character && (
                            <div className="absolute inset-0 bg-gradient-to-t from-[var(--accent)]/25 to-transparent pointer-events-none" />
                        )}
                        <User size={16} className="text-[var(--text-muted)] group-hover:text-yellow-400 transition-colors" />
                    </div>
                    <div className="text-left shrink-0">
                        <h1 className="text-xs font-black tracking-tight text-[var(--text)] group-hover:text-yellow-400 transition-colors flex items-center gap-1 whitespace-nowrap shrink-0">
                            <span className="truncate max-w-[60px] inline-block">{user?.username || 'TRAINER'}</span>
                            {character && (
                                <span 
                                    className="text-[8px] font-mono font-black px-1.5 py-0.25 rounded bg-[var(--accent)]/20 border border-[var(--accent)]/30 shadow-[0_0_10px_rgba(var(--accent-rgb),0.1)] whitespace-nowrap shrink-0"
                                    style={{ color: 'var(--accent)' }}
                                >
                                    {t('level')}.{character.totalLevel}
                                </span>
                            )}
                        </h1>
                        {character ? (
                            <div className="flex items-center gap-1 mt-0.5 whitespace-nowrap shrink-0">
                                <span className="text-[8px] text-[var(--text-muted)] font-bold font-mono whitespace-nowrap shrink-0">
                                    EXP {Math.floor(((character.totalExp ?? 0) / (character.nextLevelExp ?? 1)) * 100)}%
                                </span>
                                <div className="w-8 h-1 bg-black/60 rounded-full overflow-hidden border border-white/5 relative shrink-0">
                                    <div 
                                        className="h-full bg-[var(--accent)] rounded-full shadow-[0_0_6px_var(--accent)]"
                                        style={{ width: `${Math.min(100, Math.floor(((character.totalExp ?? 0) / (character.nextLevelExp ?? 1)) * 100))}%` }}
                                    />
                                </div>
                            </div>
                        ) : (
                            <p className="text-[8px] text-[var(--text-muted)] uppercase tracking-wider font-bold whitespace-nowrap">내 정보</p>
                        )}
                    </div>
                </Link>
            </div>

            <div className="flex items-center gap-3">
                {user && (
                    <Link 
                        to="/charge-points"
                        onClick={() => soundSynth.playClick()}
                        className="flex items-center gap-2 px-3 py-1.5 bg-[var(--card)] hover:bg-[var(--surface)] rounded-full border border-[var(--gold)]/25 hover:border-[var(--gold)] transition-all duration-300 shadow-[0_2px_6px_rgba(234,179,8,0.03)] hover:shadow-[0_2px_10px_rgba(234,179,8,0.12)] group/charge relative overflow-hidden shimmer-pill"
                    >
                        <Coins className="w-3.5 h-3.5 text-[var(--gold)] group-hover/charge:scale-110 transition-transform" />
                        <span className="text-[11px] font-black text-[var(--text)]">
                            {charLoading ? '...' : (character?.points ?? 0).toLocaleString()}
                            <span className="text-[9px] text-[var(--text-muted)] font-bold ml-0.5">P</span>
                        </span>
                        <span className="text-[9px] font-black bg-[var(--gold)] text-black px-1.5 py-0.5 rounded-full ml-0.5 shadow-[0_1.5px_3px_rgba(234,179,8,0.15)]">
                            충전
                        </span>
                    </Link>
                )}
                {isAdmin && (
                    <Link 
                        to="/admin/dashboard" 
                        onClick={() => soundSynth.playClick()}
                        className="px-3 py-1.5 bg-[#151520] hover:bg-[#ff0055] hover:text-white rounded-xl text-xs transition-all border border-white/5 font-bold text-[var(--text-muted)]"
                    >
                        관리자
                    </Link>
                )}
                <button
                    className="relative p-2 text-[var(--text-muted)] hover:text-[var(--text)] transition-colors"
                    onClick={() => {
                        soundSynth.playClick();
                        alert('새로운 알림이 없습니다.');
                    }}
                >
                    <Bell size={20} />
                </button>
                <Link 
                    to="/profile/settings" 
                    onClick={() => soundSynth.playClick()}
                    className="p-2 text-[var(--text-muted)] hover:text-[var(--text)] hover:rotate-90 transition-all duration-300"
                >
                    <Settings size={20} />
                </Link>
            </div>
        </header>
    );
}
